import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReferenceDiscount } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceDiscount';
import {
  OrderDeliveryType,
  OrderReferenceItem,
  OrderReferenceItemPrice,
  SubstituteReference,
  VehicleDetail,
} from '@1po/1po-bff-fe-spec/generated/order/response/GetDealerOrderPageResponse';
import { ExternalOrderItem } from '@1po/1po-bff-fe-spec/generated/order/response/model/ExternalOrderItem';
import { ReferenceBrandType } from '@1po/1po-bff-fe-spec/generated/order/response/model/OrderItem';
import { TFunction } from 'i18next';
import { RootState } from 'app/AppStore';
import { BrandsSearchEnginIcon, CheckCircleIcon, CloneIcon, ReplyIcon, TruckIcon } from 'assets/icons';
import { CopyToClipboardButton } from 'components/CopyToClipboardButton';
import { DataContainer } from 'components/DataContainer';
import { getDiscountTextStyle } from 'components/Discount';
import { DocumentationAlertButton } from 'components/DocumentationAlertButton';
import { MotrioWarrantyButton } from 'components/MotrioWarranty/MotrioWarrantyButton';
import { PriceErrorDisplay, PriceUnavailable } from 'components/ReferencePriceSection';
import { getTechnicalCharacteristicsLabel } from 'components/StockInfo/catalogStockUtils';
import { OrderStockDisplay } from 'components/StockInfo/StockDisplay';
import { EXADIS_PICKUP_DELIVERY_TYPE } from 'domains/basket/Basket.types';
import { useFetchSingleReference } from 'domains/catalog/Catalog.requests';
import { RENATIVE_LABEL, RENATIVE_TECHNICAL_CHARACTERISTICS_CODE } from 'domains/catalog/Catalog.types';
import { OrderItemLocal, OrderReferenceItemLocal } from 'domains/order/Order.types';
import { getDeliveryTypeLabel } from 'domains/order/OrderDelivery';
import { OrderReferenceStatus } from 'domains/order/OrderReferenceStatus';
import { DHReferenceLocal, getDHReference, getPrice } from 'domains/references';
import { useFetchPrices } from 'domains/references/References.requests';
import { BrandImage } from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/BrandImage';
import { CardPanel } from 'pages/MyOrdersPage/OrderCards/OrderCard.styled';
import { theme } from 'styles';
import { Box, Flex, Icon, Image, InfoCircleButton, MarginBox, Pipeline, Spin, Text, URL } from 'UI';
import { getData, textFormatter } from 'utils';
import { SubstituteFoot, SubstituteProducts, SubstituteTitle } from './OrderReferenceCard.styled';

export const OrderReferenceImage = ({
  referenceImageUrl,
  referenceBrand,
  vehicleDetail,
  familyCode,
  renative,
}: {
  referenceImageUrl: string | undefined;
  referenceBrand: ReferenceBrandType | undefined;
  vehicleDetail?: VehicleDetail;
  familyCode?: string;
  renative?: boolean;
}) => {
  if (referenceImageUrl || vehicleDetail?.catalogSource === 'IAM' || referenceBrand === 'IAM') {
    return (
      <Image
        alt={'reference-image'}
        height={100}
        width={100}
        src={referenceImageUrl}
        type={URL}
        dataCy={'reference-image'}
        catalogSource={vehicleDetail?.catalogSource}
        fallbackComponent={<Box width={100} height={100} background={theme.color.grey85} />}
      />
    );
  }
  return (
    <BrandImage
      referenceBrand={referenceBrand}
      vehicleBrand={vehicleDetail?.vehicleBrand}
      familyCode={familyCode}
      renative={renative}
      size={100}
    />
  );
};

export const ReplacementReference = ({ reference }: { reference: OrderReferenceItem }) => {
  const { t } = useTranslation();

  return reference.substitutes || reference.deliveredSubstitutedQuantity ? (
    <MarginBox ml={10}>
      <Flex direction={'column'}>
        {reference.substitutes?.map((s) => (
          <Text key={s.referenceNumber} type="base" color="clear_blue">
            {t(
              'order.order_detail.substituted_reference_count',
              '{{count}} out of {{total}} Substituted by Ref: {{ref}}',
              {
                count: s.deliveredQuantity,
                total: reference.quantity,
                ref: s.referenceNumber,
              },
            )}
          </Text>
        ))}
      </Flex>
    </MarginBox>
  ) : (
    <></>
  );
};

export const HeadlineSubstitute = ({ reference }: { reference: OrderReferenceItem }) => {
  const { t } = useTranslation();

  return (
    <>
      {reference.substitutes && reference.substitutes?.length > 0 && (
        <SubstituteTitle key={reference.referenceNumber}>
          <Icon IconComponent={BrandsSearchEnginIcon} color={theme.color.yellow_light} size={20} mr={15} />
          <Text key={reference.referenceNumber} type={'lead_text_white'}>
            {t(
              'order.order_detail.substituted_reference_count_detail',
              '{{count}} / {{total}} of these references have been substituted:',
              {
                count: reference.substitutes?.reduce((acc, next) => acc + (next?.deliveredQuantity ?? 0), 0) ?? 0,
                total: reference.quantity,
              },
            )}
          </Text>
        </SubstituteTitle>
      )}
    </>
  );
};

export const FootSubstitute = () => <SubstituteFoot />;

export const DeliveredQuantity = ({
  deliveredQuantity,
  deliveredSubstitutedQuantity,
}: {
  deliveredQuantity?: number;
  deliveredSubstitutedQuantity?: number;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {deliveredQuantity !== undefined && deliveredQuantity !== 0
        ? `(${t('order.reference_table.references.shipped', '{{count}} shipped', {
            count: deliveredQuantity - (deliveredSubstitutedQuantity ?? 0),
          })})`
        : ''}
    </>
  );
};

const renderPrice = (t: TFunction, price: OrderReferenceItemPrice) => (
  <Text
    type={'h5_bold'}
    decoration={price.discount ? 'line-through' : 'none'}
    data-cy={'reference-price-vat-excl'}
    disableGutter
  >
    {t('common.price.vat_exclude_price', '{{vat_exclude_price}} VAT. Excl', {
      vat_exclude_price: textFormatter.formatCurrency(price.originalUnitPriceVatExcluded, price.currency),
    })}
  </Text>
);

const renderDiscount = (t: TFunction, price: OrderReferenceItemPrice) => {
  if (price?.discount === undefined) {
    return <></>;
  }

  const displayStyle = getDiscountTextStyle({
    referenceNumber: '',
    discount: price.discount,
    promotionType: price.promotionType,
    discountType: price.discountType,
  } as ReferenceDiscount);

  return (
    <>
      <Text type="base" displayStyle={displayStyle} disableGutter>
        {t('common.price.vat_exclude_price', '{{vat_exclude_price}} VAT. Excl', {
          vat_exclude_price: textFormatter.formatCurrency(price.unitPriceVatExcluded, price.currency),
        })}
      </Text>
      <Text type="sm" bold displayStyle={displayStyle}>
        {t('order.reference.price.discount', '{{discount}}% Off', { discount: price.discount })}
      </Text>
    </>
  );
};

export const getImageUrl = (catalogReference?: DHReferenceLocal): string | undefined => {
  const images = catalogReference?.imageKeys;
  return images && images.length > 0 ? images[0] : undefined;
};

export function renderSubstituteFooter(
  substituteInlineInfo: boolean | undefined,
  reference: OrderReferenceItemLocal | OrderReferenceItem,
) {
  return (
    substituteInlineInfo !== undefined &&
    !substituteInlineInfo &&
    reference.substitutes &&
    reference.substitutes.length > 0 && <FootSubstitute />
  );
}

const OrderReferenceCard = ({
  reference,
  order,
  substituteInlineInfo,
}: {
  reference: OrderReferenceItem;
  order: OrderItemLocal | ExternalOrderItem;
  substituteInlineInfo?: boolean;
}) => {
  const { t } = useTranslation();
  const singleLineStock = reference.warehouseDeliveryStatuses && reference.warehouseDeliveryStatuses.length === 1;
  const { price } = reference;
  const referenceNumber = reference.referenceNumber;
  useFetchSingleReference(referenceNumber, null, undefined);
  const catalogReference = useSelector((state: RootState) =>
    getDHReference(state, { referenceNumber, vehicleKey: undefined }),
  );
  const catalogReferenceData = getData(catalogReference);
  const renative =
    getTechnicalCharacteristicsLabel(catalogReferenceData, RENATIVE_TECHNICAL_CHARACTERISTICS_CODE) === RENATIVE_LABEL;

  const renderStocks = (deliveryType?: OrderDeliveryType) => {
    if (!('orderDate' in order)) {
      return <></>;
    }
    return (
      <OrderStockDisplay
        orderId={order.internalOrderId}
        referenceNumber={reference.referenceNumber}
        referenceName={reference.packaging}
        warehouseDeliveryStatuses={reference.warehouseDeliveryStatuses}
        narrow
        quantity={Number(reference.quantity)}
        orderDate={order.orderDate}
        isExadisPickUp={deliveryType === EXADIS_PICKUP_DELIVERY_TYPE}
      />
    );
  };

  return (
    <>
      {substituteInlineInfo !== undefined && !substituteInlineInfo && <HeadlineSubstitute reference={reference} />}
      <MarginBox mb={15}>
        <Flex data-cy={`reference-section-${reference.referenceNumber}`}>
          <Box width={100} height={100}>
            <OrderReferenceImage
              referenceImageUrl={reference.imageUrl ?? getImageUrl(getData(catalogReference))}
              referenceBrand={getData(catalogReference)?.brand ?? reference.brand}
              vehicleDetail={'vehicleDetail' in order ? order.vehicleDetail : undefined}
              familyCode={reference.familyCode}
              renative={renative}
            />
          </Box>
          <MarginBox ml={15} />
          <Flex direction={'column'}>
            <Flex direction={'row'} align={'center'}>
              <Text type="base" bold data-cy={'order-reference'}>
                {t('catalog.reference.referenceNumber.short', 'Ref: {{referenceNumber}}', {
                  referenceNumber: reference.referenceNumber,
                })}
              </Text>
              <MarginBox mr={10} />
              <CopyToClipboardButton
                textToCopy={reference.referenceNumber}
                message={t(
                  'catalog.reference_card.reference_number.copied_to_clipboard',
                  'Reference number {{referenceNumber}} copied to clipboard',
                  { referenceNumber: reference.referenceNumber },
                )}
              />
              {substituteInlineInfo && <ReplacementReference reference={reference} />}
              {reference.referenceMark && (
                <>
                  <MarginBox mr={15} />
                  <MarginBox mt={-5}>
                    <InfoCircleButton
                      color={'grey30'}
                      tooltip={reference.referenceMark}
                      tooltipDataCy={`reference-mark-tooltip-content-${reference.referenceNumber}`}
                      data-cy={'reference-mark-icon'}
                    />
                  </MarginBox>
                </>
              )}
              <>
                <MarginBox mr={5} />
                <DocumentationAlertButton
                  reference={reference}
                  vehicleDetail={'vehicleDetail' in order ? order.vehicleDetail : undefined}
                  vehicleRegistrationNumber={
                    'vehicleDetail' in order ? order.vehicleDetail?.vrn ?? order.vehicleDetail?.vehicleKey : undefined
                  }
                  inverted
                  size={12}
                  boxSize={'xsm'}
                />
              </>
            </Flex>
            <Text type="base" color="grey35" data-cy={'designation-reference'}>
              {reference.name}
            </Text>
            {reference.sellerName && (
              <Flex direction={'row'}>
                <Icon IconComponent={TruckIcon} size={16} mt={4} mr={5} color={theme.color.info} />
                <Text type="base" color="grey35" displayStyle={'info'} data-cy={'sellerName-reference'}>
                  {reference.sellerName}
                </Text>
              </Flex>
            )}
            <Flex maxHeight={22}>
              <Text type="base" bold>
                {t('common.stock', 'Stock: ')}
              </Text>
              <MarginBox ml={5} />
              {singleLineStock && renderStocks(reference.deliveryType)}
            </Flex>
            {!singleLineStock && <MarginBox ml={15}>{renderStocks(reference.deliveryType)}</MarginBox>}
            <Flex data-cy={'reference-quantity'}>
              <Text type="base" bold>
                {t('common.quantity.short', 'Qty: ')}
              </Text>
              <MarginBox ml={5} />
              <Text type="base" color="grey35">
                {`${reference.quantity} `}
                <DeliveredQuantity
                  deliveredQuantity={reference.deliveredQuantity}
                  deliveredSubstitutedQuantity={reference.deliveredSubstitutedQuantity}
                />
              </Text>
              {reference.returnedQuantity !== undefined && reference.returnedQuantity !== 0 && (
                <MarginBox ml={5}>
                  <Icon IconComponent={ReplyIcon} size={10} color={theme.color.success} mr={3} />
                  <Text type="base" color="grey35">
                    {t('order.reference_table.references.return_status', 'Returned articles: {{ returnCount }}', {
                      returnCount: reference.returnedQuantity,
                    })}
                  </Text>
                </MarginBox>
              )}
            </Flex>
          </Flex>
          {(reference.price.clientUnitPriceVatIncluded || reference.price.clientUnitPriceVatExcluded) && (
            <Flex>
              <Text type="base" bold>
                {t('order.client_price', 'Client Price: ')}
              </Text>
              <MarginBox mr={15} />
              <Flex direction={'column'} data-cy={'reference-price-section'}>
                {reference.price.clientUnitPriceVatExcluded && (
                  <Text type="base" color="grey35" data-cy={'reference-vat-excl'}>
                    {t('order.client_price_VAT_excluded', '{{ price }} VAT. Excl', {
                      price: textFormatter.formatCurrency(
                        reference.price.clientUnitPriceVatExcluded,
                        reference.price.currency,
                      ),
                    })}
                  </Text>
                )}
                {reference.price.clientUnitPriceVatIncluded && (
                  <Text type="base" color="grey35" data-cy={'reference-vat-incl'}>
                    {t('order.client_price_VAT_included', '{{ price }} VAT. Incl', {
                      price: textFormatter.formatCurrency(
                        reference.price.clientUnitPriceVatIncluded,
                        reference.price.currency,
                      ),
                    })}
                  </Text>
                )}
              </Flex>
            </Flex>
          )}
          <Flex>
            {reference.deliveryType && (
              <Flex data-cy={'reference-delivery-method'}>
                <CardPanel>
                  <Icon
                    IconComponent={TruckIcon}
                    height={16}
                    width={16}
                    color={theme.color.brand_black}
                    display={'inline'}
                    mt={4}
                  />
                  <MarginBox ml={24}>
                    <Text type="base" bold>
                      {t('my_orders.details.delivery_method', 'Delivery method')}
                    </Text>
                  </MarginBox>
                </CardPanel>
                <CardPanel data-cy={`delivery-method-${reference.deliveryType}`}>
                  <Text type="base" color="grey35">
                    {getDeliveryTypeLabel(reference.deliveryType)}
                  </Text>
                </CardPanel>
              </Flex>
            )}
          </Flex>
          <Flex maxWidth={200} direction={'column'} align={'flex-end'}>
            {renderPrice(t, price)}
            {renderDiscount(t, price)}
            <OrderReferenceStatus referenceStatus={reference.status} orderStatus={order.orderStatus} />
            {reference.isUrgent && (
              <Flex align={'flex-end'}>
                <Flex>
                  <Icon
                    IconComponent={CheckCircleIcon}
                    color={theme.color.brand_black}
                    height={11}
                    width={11}
                    mt={1}
                    mr={5}
                  />
                  <Text type="base" color="grey35" displayStyle={'link'}>
                    {t('order.urgent', 'Urgent')}
                  </Text>
                </Flex>
              </Flex>
            )}
            <Flex />
            {reference.brand === 'MOTRIO' && (
              <MotrioWarrantyButton
                reference={reference}
                vehicleDetail={'vehicleDetail' in order ? order.vehicleDetail : undefined}
                disabled={reference.status !== 'DELIVERED' && reference.status !== 'PARTIALLY_DELIVERED'}
              />
            )}
          </Flex>
          <Box width={60} />
        </Flex>
        <MarginBox mt={15} />
        <Pipeline size={'100%'} horizontal color={theme.color.grey85} />
      </MarginBox>
      {substituteInlineInfo !== undefined && !substituteInlineInfo && (
        <>
          {reference.substitutes && reference.substitutes.length > 0 && (
            <SubstituteProducts>
              <Icon IconComponent={CloneIcon} color={theme.color.brand_black} size={20} mr={15} />
              <Text type={'h5_bold'}>{t('order.substitute_products', 'Substitute products')}</Text>
            </SubstituteProducts>
          )}
          {reference.substitutes?.map((s) => (
            <OrderReferenceSubstituteCard
              key={s.referenceNumber}
              substituteReference={s}
              isUrgent={reference.isUrgent ?? false}
              currency={reference.price.currency}
              quantity={s.deliveredQuantity}
              deliveredQuantity={s.deliveredQuantity}
              vehicleDetail={'vehicleDetail' in order ? order.vehicleDetail : undefined}
            />
          ))}
        </>
      )}
      {renderSubstituteFooter(substituteInlineInfo, reference)}
    </>
  );
};

const OrderReferenceSubstituteCard = ({
  substituteReference,
  vehicleDetail,
  isUrgent,
  currency,
  quantity,
  deliveredQuantity,
}: {
  substituteReference: SubstituteReference;
  vehicleDetail: VehicleDetail | undefined;
  isUrgent: boolean;
  currency: string;
  quantity: number;
  deliveredQuantity: number;
}) => {
  const { t } = useTranslation();
  const substituteReferenceNumber = substituteReference.referenceNumber;

  const substituteRef = useSelector((state: RootState) =>
    getDHReference(state, { referenceNumber: substituteReferenceNumber, vehicleKey: undefined }),
  );
  const substituteRefData = getData(substituteRef);
  const renative =
    getTechnicalCharacteristicsLabel(substituteRefData, RENATIVE_TECHNICAL_CHARACTERISTICS_CODE) === RENATIVE_LABEL;
  const substitutePrice = useSelector((state: RootState) => getPrice(state, substituteReferenceNumber));
  const substitutePriceData = getData(substitutePrice);

  useFetchPrices(substituteReference.referenceNumber ? [substituteReference.referenceNumber] : []);
  useFetchSingleReference(substituteReference.referenceNumber, null, undefined);

  return (
    <MarginBox mx={0} mb={15}>
      <Flex>
        <Box width={100} height={100}>
          <OrderReferenceImage
            referenceImageUrl={getImageUrl(substituteRefData)}
            referenceBrand={substituteRefData?.brand}
            vehicleDetail={vehicleDetail}
            familyCode={substituteRefData?.familyCode}
            renative={renative}
          />
        </Box>
        <MarginBox ml={15} />
        <Flex direction={'column'}>
          <Flex direction={'row'}>
            <Text type="base" bold>
              {t('catalog.reference.referenceNumber.short', 'Ref: {{referenceNumber}}', {
                referenceNumber: substituteReference.referenceNumber,
              })}
            </Text>
            <MarginBox mr={10} />
            <CopyToClipboardButton
              textToCopy={substituteReference.referenceNumber}
              message={t(
                'catalog.reference_card.reference_number.copied_to_clipboard',
                'Reference number {{referenceNumber}} copied to clipboard',
                { referenceNumber: substituteReference.referenceNumber },
              )}
            />
          </Flex>
          <DataContainer
            data={substituteRef}
            NotFound={() => <></>}
            ErrorState={() => <></>}
            Loading={() => <Spin size={'small'} />}
          >
            <Text type="base" color="grey35">
              {substituteRefData?.name}
            </Text>
          </DataContainer>
          <Flex>
            <Text type="base" bold>
              {t('common.quantity.short', 'Qty: ')}
            </Text>
            <MarginBox ml={5} />
            <Text type="base" color="grey35">
              {`${quantity} `}
              <DeliveredQuantity deliveredQuantity={deliveredQuantity} deliveredSubstitutedQuantity={undefined} />
            </Text>
            {substituteReference.returnedQuantity !== undefined && substituteReference.returnedQuantity !== 0 && (
              <MarginBox ml={5}>
                <Icon IconComponent={ReplyIcon} size={10} color={theme.color.success} mr={3} />
                <Text type="base" color="grey35">
                  {t('order.reference_table.references.return_status', 'Returned articles: {{ returnCount }}', {
                    returnCount: substituteReference.returnedQuantity,
                  })}
                </Text>
              </MarginBox>
            )}
          </Flex>
        </Flex>
        <Flex maxWidth={200} direction={'column'} align={'flex-end'}>
          <DataContainer
            data={substitutePrice}
            NotFound={() => <PriceUnavailable narrow={false} />}
            ErrorState={() => <PriceErrorDisplay narrow={false} />}
            Loading={() => <Spin size={'small'} />}
          >
            <Text type={'h5_bold'}>
              {t('common.price.vat_exclude_price', '{{vat_exclude_price}} VAT. Excl', {
                vat_exclude_price: textFormatter.formatCurrency(
                  substitutePriceData?.clientView?.recommendedPriceVatExcluded ?? 0,
                  currency,
                ),
              })}
            </Text>
          </DataContainer>
          {isUrgent && (
            <Flex align={'flex-end'}>
              <Flex>
                <Icon
                  IconComponent={CheckCircleIcon}
                  color={theme.color.brand_black}
                  height={11}
                  width={11}
                  mt={1}
                  mr={5}
                />
                <Text type="base" color="grey35" displayStyle={'link'}>
                  {t('order.urgent', 'Urgent')}
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>
        <Box width={60} />
      </Flex>
      <MarginBox mt={15} />
      <Pipeline size={'100%'} horizontal color={theme.color.grey85} />
    </MarginBox>
  );
};

export default OrderReferenceCard;
